import { useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Alert, Button, csx, Flex, IconX, Stack, Text } from '@vtex/admin-ui'
import { Drawer } from '@/components/Drawer'
import { type DownloadItemProps } from '@/components/products-listing/components/DownloadCollapser/DownloadListCollapser'
import { useExportRequests } from '@/components/products-listing/hooks/useExportRequest'

import { ExportFields } from '../components/ExportFields'
import { type ExportData } from '../components/ExportMenu/ExportMenu'

import * as style from './ExportProductAndSKUDrawer.styles'

type ExportProductAndSKUDrawerProps = {
  open: boolean
  handleClose: () => void
  exportData: ExportData
  onCreateExport: (newExport: DownloadItemProps) => void
}

export type FieldState = {
  product: string | boolean
  sku: string | boolean
}

export const ExportProductAndSKUDrawer = ({
  open,
  handleClose,
  exportData,
  onCreateExport,
}: ExportProductAndSKUDrawerProps) => {
  const { $t } = useIntl()
  const [fieldState, setFieldState] = useState<FieldState>({
    product: true,
    sku: true,
  })
  const [productSelectedFields, setProductSelectedFields] = useState<string[]>(
    []
  )
  const [skuSelectedFields, setSkuSelectedFields] = useState<string[]>([])

  const outputFields = useMemo(() => {
    return [...productSelectedFields, ...skuSelectedFields]
  }, [productSelectedFields, skuSelectedFields])

  const updatedExportData = useMemo(() => {
    return {
      ...exportData,
      OutputFields: outputFields,
    }
  }, [exportData, outputFields])

  const { postExportRequest } = useExportRequests({
    exportData: updatedExportData,
    onCreateExport: onCreateExport,
  })

  const showTemplateAlert = useMemo(() => {
    return !(fieldState.product === true && fieldState.sku === true)
  }, [fieldState])

  return (
    <Drawer
      width="560px"
      isOpen={open}
      onClose={handleClose}
      destroyOnClose
      footer={
        <>
          <Button variant="tertiary" onClick={handleClose}>
            {$t({
              id: 'admin/admin.catalog.export-drawer.cancel-button-label',
              defaultMessage: 'Cancel',
            })}
          </Button>
          <Button
            onClick={() => {
              postExportRequest('Product')
              handleClose()
            }}
          >
            {$t({
              id: 'admin/admin.catalog.export-drawer.export-button-label',
              defaultMessage: 'Export',
            })}
          </Button>
        </>
      }
      title={
        <Flex align="start">
          <Stack>
            <Text variant="display">
              {$t({
                id: 'admin/admin.catalog.export-drawer.export-button-label',
                defaultMessage: 'Export',
              })}
            </Text>
          </Stack>
          <Button
            icon={<IconX />}
            onClick={handleClose}
            variant="neutralTertiary"
            className={style.closeIcon}
            aria-label={$t({
              id: 'admin/admin.catalog.export-drawer.close-button',
              defaultMessage: 'Close',
            })}
          />
        </Flex>
      }
    >
      {showTemplateAlert && (
        <div className={csx({ padding: '0 1.5rem' })}>
          <Alert variant="warning">
            <FormattedMessage
              id="admin/admin.catalog.export-drawer.template-alert-message"
              defaultMessage="All fields must be exported to use the file as import template"
            />
          </Alert>
        </div>
      )}
      <div className={style.content}>
        <Text variant="body" tone="secondary">
          <FormattedMessage
            id="admin/admin.catalog.export-menu.select-fields-description"
            defaultMessage="Select the fields you want to export"
          />
        </Text>
      </div>
      <ExportFields
        isProduct={true}
        selectedFields={productSelectedFields}
        setSelectedFields={setProductSelectedFields}
        fieldState={fieldState}
        setFieldState={setFieldState}
      />
      <ExportFields
        isProduct={false}
        selectedFields={skuSelectedFields}
        setSelectedFields={setSkuSelectedFields}
        fieldState={fieldState}
        setFieldState={setFieldState}
      />
    </Drawer>
  )
}
